import { VFXEffectConfig } from '../../VFXEffectRegistry';
import { carousel, driftMotes, type PulseSpec } from './statusAuraBuilders';

type Primitive = VFXEffectConfig['primitives'][number];

// =============================================================================
// Zone primitive builders — the recurring grammar of world-anchored ground zones
// =============================================================================
//
// Third grammar module (spec: docs/plans/fx-grammar-specs/zone-builders.md).
// A ZONE is a persistent, WORLD-ANCHORED ground effect: placed at a point,
// follows nobody, lives for a gameplay-defined duration, and something must
// stop it. Neither sibling covers that body plan — statusAuraBuilders verbs
// ride an entity (follow/orbit), burstBuilders verbs must END on their own
// tween. Zones are ACTIVE gameplay-lifetime telegraphs; passive aftermath
// belongs to decals (decalBuilders — keep the boundary crisp).
//
//   zoneRing    — the squashed ground ellipse + rim, breathing (the floor)
//   edgeRunners — N shapes orbiting the rim (the perimeter read)
//   pulseField  — periodic radial pulse synced to the tick cadence
//   fallout     — looping area particles, rising (holy/heal) or falling (snow)
//   centerpiece — the planted object (totem pole, banner, wall slab, trap glint)
//   groundZone  — compound: floor + rim + particles + pulse in one call
//
// Rules the verbs encode:
//  - World anchoring is play-side: zone defs must NEVER set `followTarget`,
//    and zone spawns must carry `follow: false` (the §1 follow trap —
//    a casterId without it silently converts the zone into a chasing aura).
//  - Loops (`yoyo`/`repeat:-1`) are legal here ONLY because lifetime is owned
//    externally (spec §4): the placing behavior's instanceId → playPersistent
//    → `vfx:summon_expired` stop, PLUS a mandatory def-level `duration`
//    ceiling. A zone def without a working stop path is the ice_wall leak
//    again (PF-ZONE-NO-DURATION guards this).
//  - Depth: zone ground layers live UNDER entities — floors default 10-12,
//    accents stay ≤ 20 (unlike status ground rings at 49 / bursts at 93-97).
//  - Multi-tone by default (fill+rim, color+accent) so zone defs keep the
//    ≥2-distinct-fills palette-lock protection against play-time tints.
// =============================================================================

/** The zone floor — squashed ground ellipse + rim stroke, breathing.
 *  Every zone's base layer (consecration/holy_fire/smoke_bomb hand-roll #1).
 *  height defaults to width * 0.45 (the ~2:1 perspective squash). */
export function zoneRing(opts: {
  width: number; height?: number;
  fill: number; rim: number;                    // 2 tones = palette-lock safe
  alpha?: number; rimWidth?: number; depth?: number; glow?: boolean;
  pulse?: PulseSpec;
  offsetY?: number;                             // ?? 18 (feet plane, groundCloud precedent)
}): Primitive {
  const p: Primitive = {
    type: 'ellipse',
    width: opts.width,
    height: opts.height ?? Math.round(opts.width * 0.45),
    color: opts.fill,
    alpha: opts.alpha ?? 0.35,
    depth: opts.depth ?? 10,
    offsetY: opts.offsetY ?? 18,
    stroke: { color: opts.rim, width: opts.rimWidth ?? 2 },
  };
  if (opts.glow) p.glow = true;
  if (opts.pulse) {
    p.tween = {
      ...(opts.pulse.scale !== undefined ? { scale: opts.pulse.scale } : {}),
      ...(opts.pulse.alpha !== undefined ? { alpha: opts.pulse.alpha } : {}),
      duration: opts.pulse.duration, ease: 'Sine.easeInOut', yoyo: true, repeat: -1,
    };
  }
  return p;
}

/** N small shapes evenly spaced ON the rim, orbiting slowly — the perimeter
 *  read at gameplay zoom (consecration's rotating star ring, ring_of_frost,
 *  freezing_trap armed edge). Delegates to statusAuraBuilders.carousel with
 *  zone depth + rim radius — orbit centers on the SPAWN POINT when no follow
 *  target exists (OrbiterSystem gets x,y from play()), so it is already
 *  world-anchor correct. */
export function edgeRunners(opts: {
  count: number; rimRadius: number;
  shape?: 'circle' | 'star' | 'rect'; size?: number;
  color: number; alpha?: number; speed?: number;   // rot/s, default slow (0.15)
  depth?: number; glow?: boolean; flicker?: { alpha?: number; duration: number };
}): Primitive[] {
  const size = opts.size ?? 5;
  const shape = opts.shape ?? 'circle';
  return carousel({
    items: Array.from({ length: opts.count }, () => (
      shape === 'rect'
        ? { color: opts.color, width: size, height: size, alpha: opts.alpha ?? 0.85 }
        : { color: opts.color, radius: size, alpha: opts.alpha ?? 0.85 }
    )),
    shape,
    orbitRadius: opts.rimRadius,
    speed: opts.speed ?? 0.15,
    offsetY: 18,                                  // ride the floor plane
    depth: opts.depth ?? 12,
    glow: opts.glow,
    ...(opts.flicker ? { flicker: opts.flicker } : {}),
  });
}

/** Periodic radial pulse from the zone center — the TICK telegraph. Sync
 *  periodMs to the ability's tick cadence (searing 1200, healing stream 1000,
 *  AOE intervalMs) so the visual beat matches the mechanic beat. */
export function pulseField(opts: {
  radius: number; color: number; alpha?: number; depth?: number; glow?: boolean;
  periodMs: number; expandTo?: number;          // ?? 1.6
  stroke?: { color: number; width: number };
}): Primitive {
  const p: Primitive = {
    type: 'circle', radius: opts.radius,
    color: opts.color, alpha: opts.alpha ?? 0.4, depth: opts.depth ?? 11,
    offsetY: 18,
    tween: { alpha: 0, scale: opts.expandTo ?? 1.6, duration: opts.periodMs, ease: 'Sine.easeOut', repeat: -1 },
  };
  if (opts.glow) p.glow = true;
  if (opts.stroke) p.stroke = opts.stroke;
  return p;
}

/** Looping area particles over the zone: 'up' rising motes (holy, heal),
 *  'down' from spawnHeight (blizzard snow, poison drip). The looping,
 *  world-anchored sibling of burstBuilders.wispRise / statusAuraBuilders
 *  .driftMotes — delegates to driftMotes and adds zone spread + spawnHeight. */
export function fallout(opts: {
  direction: 'up' | 'down';
  count: number; spread: number; radius?: number;
  color: number; alpha?: number; depth?: number; glow?: boolean;
  travel: number; periodMs: number; delayMs?: number;
  spawnHeight?: number;                          // 'down' only: offsetY = -spawnHeight
}): Primitive {
  const p = driftMotes({
    direction: opts.direction,
    count: opts.count, spread: opts.spread, radius: opts.radius ?? 3,
    color: opts.color, alpha: opts.alpha ?? 0.8,
    depth: opts.depth ?? 12, glow: opts.glow,
    travel: opts.travel, duration: opts.periodMs, delay: opts.delayMs,
  });
  if (opts.direction === 'down' && opts.spawnHeight) p.offsetY = -opts.spawnHeight;
  return p;
}

/** The planted object at the zone center — totem pole, banner, wall slab,
 *  armed-trap glint. A vertical rect/star silhouette + optional flicker;
 *  what makes a summon read as a THING, not a puddle. flicker + sway merge
 *  into one tween (a primitive carries a single tween slot); flicker's
 *  duration wins when both are given. */
export function centerpiece(opts: {
  shape: 'rect' | 'star' | 'polygon';
  width?: number; height?: number; points?: number[];
  color: number; accent?: number;                // accent → stroke (multi-tone)
  alpha?: number; depth?: number; glow?: boolean;
  offsetY?: number;                              // plant into the ground plane
  flicker?: { alpha?: number; scale?: number; duration: number };
  sway?: { rotation: number; duration: number }; // banners/flames
}): Primitive {
  const width = opts.width ?? 14;
  const height = opts.height ?? 60;
  const base: Partial<Primitive> =
    opts.shape === 'polygon' ? { type: 'polygon', polygonPoints: opts.points ?? [] }
    : opts.shape === 'star' ? { type: 'star', points: 5, outerRadius: width, innerRadius: Math.max(2, Math.round(width * 0.45)) }
    : { type: 'rect', width, height };
  const p = {
    ...base,
    color: opts.color,
    alpha: opts.alpha ?? 0.9,
    depth: opts.depth ?? 14,
    // Plant the silhouette: base sits at the floor plane (+18, the zoneRing
    // offset), body rises above it.
    offsetY: opts.offsetY ?? (opts.shape === 'rect' ? 18 - Math.round(height / 2) : 0),
  } as Primitive;
  if (opts.accent !== undefined) p.stroke = { color: opts.accent, width: 2 };
  if (opts.glow) p.glow = true;
  if (opts.flicker || opts.sway) {
    p.tween = {
      ...(opts.flicker?.alpha !== undefined ? { alpha: opts.flicker.alpha } : {}),
      ...(opts.flicker?.scale !== undefined ? { scale: opts.flicker.scale } : {}),
      ...(opts.sway ? { rotation: opts.sway.rotation } : {}),
      duration: opts.flicker?.duration ?? opts.sway!.duration,
      ease: 'Sine.easeInOut', yoyo: true, repeat: -1,
    };
  }
  return p;
}

/** Compound: floor + rim runners + area particles in one call — the standard
 *  zone stack (precedent: shadowedSlash/stagedBlast return Primitive[]).
 *  Flavor one-offs stay literal at the call site per lane rule 1. */
export function groundZone(opts: {
  ring: Parameters<typeof zoneRing>[0];
  edge?: Parameters<typeof edgeRunners>[0];
  fall?: Parameters<typeof fallout>[0];
  pulse?: Parameters<typeof pulseField>[0];
}): Primitive[] {
  return [
    zoneRing(opts.ring),
    ...(opts.edge ? edgeRunners(opts.edge) : []),
    ...(opts.fall ? [fallout(opts.fall)] : []),
    ...(opts.pulse ? [pulseField(opts.pulse)] : []),
  ];
}
